/**
 * The DynamicLoad class provides dynamic loading functionality for different grid types (product, collection, article).
 * This class contains common properties and methods to be inherited by subclasses.
 */
class DynamicLoad extends HTMLElement {
  constructor() {
    super();

    // Initialize with null values - will be set in connectedCallback
    this.itemGrid = null;
    this.gridType = null;
    this.retryCount = 0;
    this.maxRetries = 10; // Maximum number of retry attempts
    this.isInitialized = false; // Flag to prevent multiple initializations
    this.retryTimeout = null; // Store timeout to clear if needed
  }

    connectedCallback() {
      // Prevent multiple initializations
      if (this.isInitialized) {
        return;
      }

      // Add global error handler for this component to catch uncaught errors
      if (!this._errorHandlerAdded) {
        this._errorHandlerAdded = true;
        this._globalErrorHandler = (event) => {
          // #region agent log
          fetch('http://127.0.0.1:7242/ingest/59ce887d-fab5-4149-944f-f914fe874f3e',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'dynamic-load-more.js:18',message:'Global error caught',data:{message:event.message,filename:event.filename,lineno:event.lineno,error:event.error?.message},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'K'})}).catch(()=>{});
          // #endregion
          // Only log, don't prevent default - let browser handle it
        };
        window.addEventListener('error', this._globalErrorHandler);
        window.addEventListener('unhandledrejection', (event) => {
          // #region agent log
          fetch('http://127.0.0.1:7242/ingest/59ce887d-fab5-4149-944f-f914fe874f3e',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'dynamic-load-more.js:25',message:'Unhandled promise rejection',data:{reason:event.reason?.message||String(event.reason)},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'K'})}).catch(()=>{});
          // #endregion
        });
      }

      // Wait for DOM to be ready before initializing
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.initialize());
      } else {
        this.initialize();
      }
    }

    disconnectedCallback() {
      // Clean up when element is removed
      if (this.retryTimeout) {
        clearTimeout(this.retryTimeout);
        this.retryTimeout = null;
      }
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      if (this.observerTimeout) {
        clearTimeout(this.observerTimeout);
        this.observerTimeout = null;
      }
      if (this.sectionLoadHandler) {
        window.removeEventListener("shopify:section:load", this.sectionLoadHandler);
      }
      if (this.clickHandler && this.btnLoadMore) {
        this.btnLoadMore.removeEventListener("click", this.clickHandler);
      }
      this.isInitialized = false;
      this.retryCount = 0;
      this.isLoading = false;
    }

  initialize() {
    // Prevent multiple initializations
    if (this.isInitialized && this.itemGrid) {
      return;
    }

    // Selects DOM elements for different grid types
    this.itemGrid =
      document.querySelector("#product-grid") ||
      document.querySelector("#collection-grid") ||
      document.querySelector("#article-grid");

    // Determines the grid type
    if (this.itemGrid) {
      if (this.itemGrid.id === "collection-grid") {
        this.gridType = "collection";
      } else if (this.itemGrid.id === "product-grid") {
        this.gridType = "product";
      } else if (this.itemGrid.id === "article-grid") {
        this.gridType = "article";
      } else {
        this.gridType = "unknown";
      }
      // Reset retry count on success
      this.retryCount = 0;
      this.isInitialized = true;
    } else {
      // Check if we've exceeded max retries
      if (this.retryCount >= this.maxRetries) {
        console.error("DynamicLoad: Grid element not found after", this.maxRetries, "retries. Component will not initialize.");
        return;
      }
      
      this.retryCount++;
      // Clear any existing timeout
      if (this.retryTimeout) {
        clearTimeout(this.retryTimeout);
      }
      // Retry after a short delay in case DOM isn't fully ready
      this.retryTimeout = setTimeout(() => {
        this.initialize();
      }, 100);
      return;
    }

    this.setProperties();
  }

  /**
   * A placeholder method for subclasses to set additional properties.
   */
  setProperties() {
    // Additional properties can be set here if needed
  }

  /**
   * Appends newly loaded content to the existing grid.
   * @param {string} newContent - The HTML content to append.
   * @param {boolean} prepend - Determines whether to prepend or append the content.
   */
  appendContent(newContent, prepend = false) {
    if (!this.itemGrid) {
      console.error("DynamicLoad: Cannot append content - grid element not found");
      return;
    }

    if (prepend) {
      this.itemGrid.insertAdjacentHTML("afterbegin", newContent);
    } else {
      this.itemGrid.insertAdjacentHTML("beforeend", newContent);
    }
  }

  /**
   * Updates the page URL.
   * @param {number} pageNumber - The new page number.
   */
  updateUrl(pageNumber) {
    const currentUrl = new URL(window.location.href);
    const currentUrlParams = new URLSearchParams(currentUrl.search);

    if (pageNumber === 1) {
      currentUrlParams.delete("page");
    } else {
      currentUrlParams.set("page", pageNumber);
    }

    currentUrl.search = currentUrlParams.toString();
    history.replaceState({}, "", currentUrl.toString());
  }

  /**
   * Updates the product/collection/article count.
   */
  updateItemCount() {
    if (!this.itemGrid || !this.gridType) {
      return; // Grid not initialized yet
    }

    let selector;
    switch (this.gridType) {
      case "product":
        selector = ".product-card";
        break;
      case "collection":
        selector = ".card-collection";
        break;
      case "article":
        selector = ".card-article";
        break;
      default:
        selector = ".card-item";
    }

    // Counts items within the itemGrid
    const itemCount = this.itemGrid.querySelectorAll(selector).length;

    if (this.gridType !== 'product') return;

    const itemCountOfAll = document.querySelector(
      `.collection-facets__product-count .product-count-of-all`
    );

    if (itemCountOfAll) {
      itemCountOfAll.innerText = itemCount;
    }
  }
}

// DynamicLoadMore Custom Element
if (!customElements.get("dynamic-load-more")) {
  class DynamicLoadMore extends DynamicLoad {
    constructor() {
      super();
      this.btnLoadMore = null;
      this.observer = null;
      this.isInfinityScrollActive = false;
      this.sectionLoadHandler = null;
      this.clickHandler = null;
      this.propertiesSet = false;
      this.isLoading = false; // Flag to prevent multiple simultaneous loads
      this.lastLoadTime = 0; // Track last load time for throttling
      this.minLoadInterval = 2000; // Minimum 2 seconds between loads (mobile Safari protection)
      this.observerTimeout = null; // Debounce timeout for observer
    }

    disconnectedCallback() {
      // Clean up observer and event listeners
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      if (this.observerTimeout) {
        clearTimeout(this.observerTimeout);
        this.observerTimeout = null;
      }
      if (this.sectionLoadHandler) {
        window.removeEventListener("shopify:section:load", this.sectionLoadHandler);
      }
      if (this.clickHandler && this.btnLoadMore) {
        this.btnLoadMore.removeEventListener("click", this.clickHandler);
      }
      if (this._globalErrorHandler) {
        window.removeEventListener('error', this._globalErrorHandler);
        this._globalErrorHandler = null;
        this._errorHandlerAdded = false;
      }
      this.isLoading = false;
      this.propertiesSet = false;
      super.disconnectedCallback();
    }

    setProperties() {
      // Prevent multiple property setups
      if (this.propertiesSet && this.itemGrid) {
        return;
      }

      super.setProperties();
      
      if (!this.itemGrid) {
        return; // Wait for grid to be initialized
      }

      this.btnLoadMore = this.querySelector(".js-btn-load-more");
      
      if (!this.btnLoadMore) {
        console.warn("DynamicLoadMore: Load more button not found");
        return;
      }

      this.toggleItemsVisibility();

      // Updates properties and visibility when a Shopify section is loaded
      // Remove existing listener if it exists to prevent duplicates
      if (this.sectionLoadHandler) {
        window.removeEventListener("shopify:section:load", this.sectionLoadHandler);
      }
      this.sectionLoadHandler = () => {
        // Reset properties flag when section reloads
        this.propertiesSet = false;
        this.setProperties();
        this.toggleItemsVisibility();
      };
      window.addEventListener("shopify:section:load", this.sectionLoadHandler);

      this.updateItemCount();

      // Remove existing click listener if it exists to prevent duplicates
      if (this.clickHandler) {
        this.btnLoadMore.removeEventListener("click", this.clickHandler);
      }
      this.clickHandler = (e) => {
        e.preventDefault();
        this.loadMoreItems();
      };
      this.btnLoadMore.addEventListener("click", this.clickHandler);

      // Check and initialize Infinity Scroll
      this.checkAndSetupInfinityScroll();
      
      // Mark properties as set
      this.propertiesSet = true;
    }

    /**
     * Checks if infinity scroll should be set up and initializes it.
     */
    checkAndSetupInfinityScroll() {
      if (this.itemGrid) {
        const containerId =
          this.gridType === 'product' ? 'ProductGridContainer' :
          this.gridType === 'collection' ? 'CollectionGridContainer' :
          this.gridType === 'article' ? 'ArticleGridContainer' :
          null;

        if (containerId) {
          const parentContainer = this.itemGrid.closest(`#${containerId}`);

          // Detect mobile Safari
          const isMobileSafari = /iPhone|iPad|iPod/.test(navigator.userAgent) && 
                                 /Safari/.test(navigator.userAgent) && 
                                 !/CriOS|FxiOS|OPiOS/.test(navigator.userAgent);

          // CRITICAL FIX: Disable infinity scroll on mobile Safari to prevent repeated prompts
          // Prestige theme works because it doesn't use infinity scroll - this matches that behavior
          if (isMobileSafari) {
            console.log("Infinity Scroll disabled on mobile Safari to prevent issues");
            this.isInfinityScrollActive = false;
            return;
          }

          if (parentContainer && parentContainer.classList.contains('infinity-scroll-active')) {
            console.log("Infinity Scroll is active for:", this.gridType);
            this.isInfinityScrollActive = true;
            this.setupInfinityScroll();
          } else {
            this.isInfinityScrollActive = false;
          }
        } else {
          this.isInfinityScrollActive = false;
        }
      } else {
        this.isInfinityScrollActive = false;
      }
    }

    /**
     * Sets up the Intersection Observer for infinite scrolling.
     * The observer will trigger a click on the "Load More" button when it becomes visible.
     * Includes mobile Safari-specific protections against rapid-fire triggers.
     */
    setupInfinityScroll() {
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/59ce887d-fab5-4149-944f-f914fe874f3e',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'dynamic-load-more.js:313',message:'setupInfinityScroll called',data:{hasButton:!!this.btnLoadMore,hasObserver:!!this.observer,userAgent:navigator.userAgent},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{});
      // #endregion

      if (!this.btnLoadMore) {
        return;
      }

      // Disconnect existing observer if it exists
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }

      // Clear any pending observer timeout
      if (this.observerTimeout) {
        clearTimeout(this.observerTimeout);
        this.observerTimeout = null;
      }

      // Detect mobile Safari for special handling
      const isMobileSafari = /iPhone|iPad|iPod/.test(navigator.userAgent) && 
                             /Safari/.test(navigator.userAgent) && 
                             !/CriOS|FxiOS|OPiOS/.test(navigator.userAgent);

      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/59ce887d-fab5-4149-944f-f914fe874f3e',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'dynamic-load-more.js:331',message:'Mobile Safari detection',data:{isMobileSafari,userAgent:navigator.userAgent},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'G'})}).catch(()=>{});
      // #endregion

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          try {
            // #region agent log
            fetch('http://127.0.0.1:7242/ingest/59ce887d-fab5-4149-944f-f914fe874f3e',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'dynamic-load-more.js:335',message:'IntersectionObserver callback fired',data:{isIntersecting:entry.isIntersecting,isMobileSafari,hasTimeout:!!this.observerTimeout,isLoading:this.isLoading,isDisabled:this.btnLoadMore?.hasAttribute('disabled'),hasLoadingClass:this.btnLoadMore?.classList.contains('loading'),timeSinceLastLoad:Date.now()-this.lastLoadTime},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{});
            // #endregion

            // CRITICAL: Validate button exists before proceeding
            if (!this.btnLoadMore) {
              // #region agent log
              fetch('http://127.0.0.1:7242/ingest/59ce887d-fab5-4149-944f-f914fe874f3e',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'dynamic-load-more.js:343',message:'Skipped: button not found',data:{},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'H'})}).catch(()=>{});
              // #endregion
              return;
            }

            // Check debounce for mobile Safari FIRST (before any other checks)
            if (isMobileSafari && this.observerTimeout) {
              // #region agent log
              fetch('http://127.0.0.1:7242/ingest/59ce887d-fab5-4149-944f-f914fe874f3e',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'dynamic-load-more.js:350',message:'Skipped: debounce active (early check)',data:{isMobileSafari},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'})}).catch(()=>{});
              // #endregion
              return; // Skip if debounce is already active
            }

            // Trigger click only if the button is intersecting and not currently disabled/loading
            if (entry.isIntersecting && 
                !this.btnLoadMore.hasAttribute('disabled') &&
                !this.btnLoadMore.classList.contains('loading') &&
                !this.isLoading) {
              
              // Throttle: Check if enough time has passed since last load
              const now = Date.now();
              const timeSinceLastLoad = now - this.lastLoadTime;
              if (timeSinceLastLoad < this.minLoadInterval) {
                // #region agent log
                fetch('http://127.0.0.1:7242/ingest/59ce887d-fab5-4149-944f-f914fe874f3e',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'dynamic-load-more.js:366',message:'Skipped: throttle interval not met',data:{timeSinceLastLoad,minLoadInterval:this.minLoadInterval},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'C'})}).catch(()=>{});
                // #endregion
                // DON'T clear timeout here - let it expire naturally to prevent rapid retries
                return; // Too soon, skip
              }

              // Set loading flags BEFORE triggering click to prevent race conditions
              this.isLoading = true;
              this.lastLoadTime = now;
              this.btnLoadMore.classList.add('loading');
              this.btnLoadMore.setAttribute('disabled', 'true');

              // Set debounce timeout ONLY when we're about to trigger (for mobile Safari)
              if (isMobileSafari) {
                // Clear any existing timeout first
                if (this.observerTimeout) {
                  clearTimeout(this.observerTimeout);
                }
                // Set timeout to block subsequent rapid callbacks
                this.observerTimeout = setTimeout(() => {
                  this.observerTimeout = null;
                }, 2000); // 2 seconds debounce
              }

              // #region agent log
              fetch('http://127.0.0.1:7242/ingest/59ce887d-fab5-4149-944f-f914fe874f3e',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'dynamic-load-more.js:378',message:'Triggering load more click',data:{isMobileSafari,isLoading:this.isLoading,lastLoadTime:this.lastLoadTime},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{});
              // #endregion

              // Trigger the load - wrap in try-catch to prevent errors from breaking the observer
              try {
                this.btnLoadMore.click();
              } catch (clickError) {
                // #region agent log
                fetch('http://127.0.0.1:7242/ingest/59ce887d-fab5-4149-944f-f914fe874f3e',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'dynamic-load-more.js:390',message:'Click error caught',data:{errorMessage:clickError.message,errorStack:clickError.stack},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'I'})}).catch(()=>{});
                // #endregion
                console.error('DynamicLoadMore: Error triggering click:', clickError);
                // Reset loading state on error
                this.isLoading = false;
                if (this.btnLoadMore) {
                  this.btnLoadMore.removeAttribute('disabled');
                  this.btnLoadMore.classList.remove('loading');
                }
                // Clear timeout on error
                if (isMobileSafari && this.observerTimeout) {
                  clearTimeout(this.observerTimeout);
                  this.observerTimeout = null;
                }
              }
            } else {
              // #region agent log
              fetch('http://127.0.0.1:7242/ingest/59ce887d-fab5-4149-944f-f914fe874f3e',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'dynamic-load-more.js:343',message:'Skipped: conditions not met',data:{isIntersecting:entry.isIntersecting,isDisabled:this.btnLoadMore?.hasAttribute('disabled'),hasLoadingClass:this.btnLoadMore?.classList.contains('loading'),isLoading:this.isLoading},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'D'})}).catch(()=>{});
              // #endregion
              // DON'T clear timeout when not intersecting - let it expire naturally
            }
          } catch (observerError) {
            // #region agent log
            fetch('http://127.0.0.1:7242/ingest/59ce887d-fab5-4149-944f-f914fe874f3e',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'dynamic-load-more.js:408',message:'Observer callback error',data:{errorMessage:observerError.message,errorStack:observerError.stack},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'J'})}).catch(()=>{});
            // #endregion
            console.error('DynamicLoadMore: Error in IntersectionObserver callback:', observerError);
            // Reset state on error
            if (isMobileSafari && this.observerTimeout) {
              clearTimeout(this.observerTimeout);
              this.observerTimeout = null;
            }
          }
        });
      }, {
        root: null,
        // Increased rootMargin for mobile Safari to account for viewport changes
        rootMargin: isMobileSafari ? '100px' : '0px',
        threshold: isMobileSafari ? 0.01 : 0.1 // Lower threshold for mobile Safari
      });

      this.observer.observe(this.btnLoadMore);

      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/59ce887d-fab5-4149-944f-f914fe874f3e',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'dynamic-load-more.js:377',message:'IntersectionObserver setup complete',data:{isMobileSafari,rootMargin:isMobileSafari?'100px':'0px',threshold:isMobileSafari?0.01:0.1},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{});
      // #endregion
    }

    /**
     * Controls the visibility of the "Load More" button.
     */
    toggleItemsVisibility() {
      if (!this.btnLoadMore) {
        return; // Button not initialized yet
      }

      const nextPageLink = this.btnLoadMore.dataset.href;
      if (!nextPageLink) {
        this.btnLoadMore.style.display = "none";
      } else {
        this.btnLoadMore.style.display = "";
      }
    }

    /**
     * Loads more products/collections/articles.
     */
    async loadMoreItems() {
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/59ce887d-fab5-4149-944f-f914fe874f3e',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'dynamic-load-more.js:399',message:'loadMoreItems called',data:{isLoading:this.isLoading,hasButton:!!this.btnLoadMore},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{});
      // #endregion

      // Prevent multiple simultaneous loads
      if (this.isLoading) {
        // #region agent log
        fetch('http://127.0.0.1:7242/ingest/59ce887d-fab5-4149-944f-f914fe874f3e',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'dynamic-load-more.js:401',message:'Skipped: already loading',data:{isLoading:this.isLoading},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'E'})}).catch(()=>{});
        // #endregion
        console.log("DynamicLoadMore: Already loading, skipping duplicate request");
        return;
      }

      this.isLoading = true;
      this.btnLoadMore.setAttribute("disabled", "true");
      const nextPageLink = this.btnLoadMore.dataset.href;

      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/59ce887d-fab5-4149-944f-f914fe874f3e',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'dynamic-load-more.js:408',message:'Starting load more request',data:{hasNextPageLink:!!nextPageLink,nextPageLink},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{});
      // #endregion

      if (!nextPageLink) {
        this.btnLoadMore.removeAttribute("disabled");
        return;
      }

      try {
        // #region agent log
        fetch('http://127.0.0.1:7242/ingest/59ce887d-fab5-4149-944f-f914fe874f3e',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'dynamic-load-more.js:460',message:'Starting fetch request',data:{url:nextPageLink},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'F'})}).catch(()=>{});
        // #endregion

        const nextPageResponse = await fetch(nextPageLink).catch(fetchError => {
          // #region agent log
          fetch('http://127.0.0.1:7242/ingest/59ce887d-fab5-4149-944f-f914fe874f3e',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'dynamic-load-more.js:463',message:'Fetch network error',data:{errorMessage:fetchError.message,url:nextPageLink},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'F'})}).catch(()=>{});
          // #endregion
          throw new Error(`Network error: ${fetchError.message}`);
        });
        
        if (!nextPageResponse.ok) {
          // #region agent log
          fetch('http://127.0.0.1:7242/ingest/59ce887d-fab5-4149-944f-f914fe874f3e',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'dynamic-load-more.js:468',message:'Fetch HTTP error',data:{status:nextPageResponse.status,statusText:nextPageResponse.statusText,url:nextPageLink},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'F'})}).catch(()=>{});
          // #endregion
          throw new Error(`HTTP error! status: ${nextPageResponse.status}`);
        }
        
        const nextPageText = await nextPageResponse.text();

        if (!nextPageText || nextPageText.trim().length === 0) {
          throw new Error('Empty response received from server');
        }

        const nextPageHTML = new DOMParser().parseFromString(
          nextPageText,
          "text/html"
        );

        // Find the relevant grid element from the new page
        let newGridContainer;
        if (this.gridType === "product") {
          newGridContainer = nextPageHTML.querySelector("#ProductGridContainer");
        } else if (this.gridType === "collection") {
          newGridContainer = nextPageHTML.querySelector("#CollectionGridContainer");
        } else if (this.gridType === "article") {
          newGridContainer = nextPageHTML.querySelector("#ArticleGridContainer");
        }

        const newGrid = newGridContainer
          ? newGridContainer.querySelector(`#${this.gridType}-grid`)
          : null;

        if (newGrid) {
          this.appendContent(newGrid.innerHTML);
        } else {
          console.warn(`New grid element (#${this.gridType}-grid) not found in fetched page.`);
        }

        // After appending content, re-query for the button to ensure it's the current one
        this.btnLoadMore = this.querySelector(".js-btn-load-more");
        if (!this.btnLoadMore) {
          console.error("Load More button disappeared after content append. Cannot continue.");
          return;
        }

        // Find the next page button and update its href
        const nextButtonOnNewPage = nextPageHTML.querySelector(
          ".js-btn-load-more"
        );
        if (nextButtonOnNewPage) {
          const nextPageLinkNew = nextButtonOnNewPage.dataset.href;
          this.btnLoadMore.dataset.href = nextPageLinkNew;
        } else {
          this.btnLoadMore.dataset.href = "";
        }

        // Update the URL
        const currentUrl = new URL(window.location.href);
        const currentUrlParams = new URLSearchParams(currentUrl.search);
        const currentPage = parseInt(currentUrlParams.get("page"), 10) || 1;
        this.updateUrl(currentPage + 1);

        this.updateItemCount();
        this.toggleItemsVisibility();

        // If infinity scroll is active, re-setup the observer for the potentially new button
        // But only if there's a next page available
        if (this.isInfinityScrollActive && this.btnLoadMore.dataset.href) {
          // Small delay to ensure DOM is updated
          setTimeout(() => {
            this.setupInfinityScroll();
          }, 100);
        } else if (this.isInfinityScrollActive && !this.btnLoadMore.dataset.href) {
          // No more pages, disconnect observer
          if (this.observer) {
            this.observer.disconnect();
            this.observer = null;
          }
        }

      } catch (error) {
        // #region agent log
        fetch('http://127.0.0.1:7242/ingest/59ce887d-fab5-4149-944f-f914fe874f3e',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'dynamic-load-more.js:417',message:'Load more error caught',data:{errorMessage:error.message,errorStack:error.stack},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'F'})}).catch(()=>{});
        // #endregion
        console.error("Load more error:", error);
        // Show error message to the user
        const errorMessage = document.createElement('div');
        errorMessage.className = 'error-message p-4 bg-red-100 border border-red-400 text-red-700 rounded-md mt-4';
        errorMessage.textContent = `Unable to load more products. Please try again.`;
        errorMessage.setAttribute('role', 'alert');
        
        // Remove any existing error messages first
        const existingError = this.querySelector('.error-message');
        if (existingError) {
          existingError.remove();
        }
        
        this.btnLoadMore.after(errorMessage);
        setTimeout(() => errorMessage.remove(), 5000);
      } finally {
        // #region agent log
        fetch('http://127.0.0.1:7242/ingest/59ce887d-fab5-4149-944f-f914fe874f3e',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'dynamic-load-more.js:434',message:'loadMoreItems completed',data:{isLoading:this.isLoading},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{});
        // #endregion
        this.isLoading = false;
        if (this.btnLoadMore) {
          this.btnLoadMore.removeAttribute("disabled");
          this.btnLoadMore.classList.remove('loading');
        }
      }
    }
  }

  customElements.define("dynamic-load-more", DynamicLoadMore);
}

// DynamicLoadPrevious Custom Element
if (!customElements.get("dynamic-load-previous")) {
  class DynamicLoadPrevious extends DynamicLoad {
    constructor() {
      super();
      this.btnLoadPrevious = null;
      this.sectionLoadHandler = null;
      this.clickHandler = null;
      this.propertiesSet = false;
    }

    setProperties() {
      // Prevent multiple property setups
      if (this.propertiesSet && this.itemGrid) {
        return;
      }

      super.setProperties();
      
      if (!this.itemGrid) {
        return; // Wait for grid to be initialized
      }

      this.btnLoadPrevious = this.querySelector(".js-btn-load-previous");
      
      if (!this.btnLoadPrevious) {
        console.warn("DynamicLoadPrevious: Load previous button not found");
        return;
      }

      this.checkPageParameter();

      // Updates properties and page parameter when a Shopify section is loaded
      // Remove existing listener if it exists to prevent duplicates
      if (this.sectionLoadHandler) {
        window.removeEventListener("shopify:section:load", this.sectionLoadHandler);
      }
      this.sectionLoadHandler = () => {
        // Reset properties flag when section reloads
        this.propertiesSet = false;
        this.setProperties();
        this.checkPageParameter();
      };
      window.addEventListener("shopify:section:load", this.sectionLoadHandler);

      this.updateItemCount();

      // Remove existing click listener if it exists to prevent duplicates
      if (this.clickHandler) {
        this.btnLoadPrevious.removeEventListener("click", this.clickHandler);
      }
      this.clickHandler = (e) => {
        e.preventDefault();
        this.loadPreviousItems();
      };
      this.btnLoadPrevious.addEventListener("click", this.clickHandler);
      
      // Mark properties as set
      this.propertiesSet = true;
    }

    /**
     * Checks the page parameter and sets the visibility of the "Load Previous" button.
     */
    checkPageParameter() {
      const currentUrl = new URL(window.location.href);
      const currentUrlParams = new URLSearchParams(currentUrl.search);
      const currentPage = parseInt(currentUrlParams.get("page"), 10) || 1;

      if (currentPage <= 1) {
        this.btnLoadPrevious.style.display = "none";
      } else {
        this.btnLoadPrevious.style.display = "";
      }
    }

    /**
     * Loads previous products/collections/articles.
     */
    async loadPreviousItems() {
      this.btnLoadPrevious.setAttribute("disabled", "true");
      const prevPageLink = this.btnLoadPrevious.dataset.href;

      if (!prevPageLink) {
        this.btnLoadPrevious.removeAttribute("disabled");
        return;
      }

      try {
        const prevPageResponse = await fetch(prevPageLink);
        
        if (!prevPageResponse.ok) {
          throw new Error(`HTTP error! status: ${prevPageResponse.status}`);
        }
        
        const prevPageText = await prevPageResponse.text();

        if (!prevPageText || prevPageText.trim().length === 0) {
          throw new Error('Empty response received from server');
        }

        const prevPageHTML = new DOMParser().parseFromString(
          prevPageText,
          "text/html"
        );

        // Find the relevant grid element from the new page
        let newGridContainer;
        if (this.gridType === "product") {
          newGridContainer = prevPageHTML.querySelector("#ProductGridContainer");
        } else if (this.gridType === "collection") {
          newGridContainer = prevPageHTML.querySelector("#CollectionGridContainer");
        } else if (this.gridType === "article") {
          newGridContainer = prevPageHTML.querySelector("#ArticleGridContainer");
        }

        const newGrid = newGridContainer
          ? newGridContainer.querySelector(`#${this.gridType}-grid`)
          : null;

        if (newGrid) {
          this.appendContent(newGrid.innerHTML, true);
        } else {
          console.warn(`New grid element (#${this.gridType}-grid) not found in fetched page.`);
        }

        // After appending content, re-query for the button to ensure it's the current one
        this.btnLoadPrevious = this.querySelector(".js-btn-load-previous");
        if (!this.btnLoadPrevious) {
          console.error("Load Previous button disappeared after content prepend. Cannot continue.");
          return;
        }

        // Find the previous page button and update its href
        const prevButtonOnNewPage = prevPageHTML.querySelector(
          ".js-btn-load-previous"
        );
        if (prevButtonOnNewPage) {
          const prevPageLinkNew = prevButtonOnNewPage.dataset.href;
          this.btnLoadPrevious.dataset.href = prevPageLinkNew;
        } else {
          this.btnLoadPrevious.dataset.href = "";
        }

        // Update the URL
        const currentUrl = new URL(window.location.href);
        const currentUrlParams = new URLSearchParams(currentUrl.search);
        const currentPage = parseInt(currentUrlParams.get("page"), 10) || 1;
        this.updateUrl(currentPage - 1);

        this.checkPageParameter();
        this.updateItemCount();
      } catch (error) {
        console.error("Load previous error:", error);
        // Show error message to the user
        const errorMessage = document.createElement('div');
        errorMessage.className = 'error-message p-4 bg-red-100 border border-red-400 text-red-700 rounded-md mt-4';
        errorMessage.textContent = `Unable to load previous products. Please try again.`;
        errorMessage.setAttribute('role', 'alert');
        
        // Remove any existing error messages first
        const existingError = this.querySelector('.error-message');
        if (existingError) {
          existingError.remove();
        }
        
        this.btnLoadPrevious.after(errorMessage);
        setTimeout(() => errorMessage.remove(), 5000);
      } finally {
        if (this.btnLoadPrevious) {
          this.btnLoadPrevious.removeAttribute("disabled");
        }
      }
    }
  }

  customElements.define("dynamic-load-previous", DynamicLoadPrevious);
}
